import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Loader2, MailCheck, Plus, Send, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail } from "@/lib/api";

const KIND = { problem: "Problem", reminder: "Reminder", resolved: "Back to normal", test: "Test" };

/** Settings → LLM Keys → key-failure e-mails (super admins + extra addresses). */
export function KeyAlertsCard({ onChecked }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(null);

    const load = async () => setData((await api.get("/admin/llm-keys/alerts")).data);
    useEffect(() => { load().catch(() => toast.error("Failed to load alert settings")); }, []);

    const run = async (what, fn) => {
        setBusy(what);
        try {
            await fn();
            await load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Request failed");
        } finally {
            setBusy(null);
        }
    };
    const save = (patch, msg) => run("save", async () => { await api.put("/admin/llm-keys/alerts", patch); toast.success(msg); });
    const check = () => run("check", async () => {
        const { data: r } = await api.post("/admin/llm-keys/alerts/check");
        const mail = r.email ? (r.sent ? `${KIND[r.email]} e-mail sent to ${r.delivered}` : `${KIND[r.email]} e-mail not sent: ${r.error}`) : "no e-mail due";
        (r.email && !r.sent ? toast.error : toast.success)(`Check done — ${r.open_issues} open problem(s), ${mail}`);
        onChecked?.();
    });
    const test = () => run("test", async () => {
        const { data: r } = await api.post("/admin/llm-keys/alerts/test");
        (r.sent ? toast.success : toast.error)(r.sent ? `Test e-mail sent to ${r.delivered.join(", ")}` : `Test e-mail failed: ${r.error}`);
    });

    if (!data) return <div className="mt-8 text-[11px] text-white/45" data-testid="llm-alerts-loading">Loading alert settings…</div>;
    return (
        <section className="mt-8 bg-[#0a0a0a] border border-white/10 rounded-2xl p-6" data-testid="llm-alerts-card">
            <header className="flex items-start justify-between gap-3 flex-wrap mb-4">
                <div>
                    <h2 className="text-sm font-semibold text-white inline-flex items-center gap-2"><BellRing className="w-4 h-4 text-violet-300" /> Key failure e-mails</h2>
                    <p className="text-[12px] text-white/50 mt-1 max-w-xl">
                        Checked every {data.check_every_minutes} min. E-mails go out when a problem appears, every {data.reminder_hours} h while it stays open, and once it's fixed.
                    </p>
                </div>
                <label className="inline-flex items-center gap-2 text-[12px] text-white/70">
                    <Switch checked={data.enabled} onCheckedChange={(v) => save({ enabled: v }, v ? "Key failure e-mails on" : "Key failure e-mails off")}
                            disabled={busy === "save"} data-testid="llm-alerts-enabled-switch" />
                    {data.enabled ? "On" : "Off"}
                </label>
            </header>
            {!data.email_configured && <p className="mb-3 text-[12px] text-amber-200" data-testid="llm-alerts-email-missing">RESEND_API_KEY is not set — e-mails can't be sent.</p>}
            <Recipients data={data} onSave={(list) => save({ extra_recipients: list }, "Recipients saved")} busy={busy === "save"} />
            <div className="mt-4 flex gap-2 flex-wrap">
                <ActionButton onClick={check} busy={busy === "check"} icon={MailCheck} testId="llm-alerts-check-btn">Check now</ActionButton>
                <ActionButton onClick={test} busy={busy === "test"} icon={Send} testId="llm-alerts-test-btn">Send test e-mail</ActionButton>
            </div>
            <AlertState state={data.state} />
            <RecentSends recent={data.recent} />
        </section>
    );
}

function ActionButton({ onClick, busy, icon: Icon, testId, children }) {
    return (
        <button onClick={onClick} disabled={busy} data-testid={testId}
                className="px-3 py-1.5 border border-white/15 rounded-md text-[12px] hover:bg-white/5 inline-flex items-center gap-1.5 disabled:opacity-40 transition-colors">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />} {children}
        </button>
    );
}

function Recipients({ data, onSave, busy }) {
    const [value, setValue] = useState("");
    const extra = data.extra_recipients;
    const add = () => { const v = value.trim(); if (v) { onSave([...extra, v]); setValue(""); } };
    return (
        <div className="space-y-2" data-testid="llm-alerts-recipients">
            <div className="text-[11px] text-white/45 uppercase tracking-wider">Recipients</div>
            <div className="flex flex-wrap gap-1.5">
                {data.recipients.super_admins.map((e) => (
                    <span key={e} className="px-2 py-0.5 rounded-full bg-white/[0.06] text-[11px] text-white/75" data-testid={`llm-alerts-super-${e}`}>{e} · super admin</span>
                ))}
                {extra.map((e) => (
                    <span key={e} className="pl-2 pr-1 py-0.5 rounded-full bg-violet-400/10 border border-violet-300/20 text-[11px] text-violet-100 inline-flex items-center gap-1" data-testid={`llm-alerts-extra-${e}`}>
                        {e}
                        <button onClick={() => onSave(extra.filter((x) => x !== e))} disabled={busy} aria-label={`Remove ${e}`} data-testid={`llm-alerts-remove-${e}`}
                                className="p-0.5 rounded-full hover:bg-white/10"><X className="w-3 h-3" /></button>
                    </span>
                ))}
            </div>
            <div className="flex gap-2 max-w-md">
                <input value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="ops@yourcompany.com"
                       className="flex-1 min-w-0 bg-black/40 border border-white/15 rounded-md px-2.5 py-1.5 text-[12px] focus:outline-none focus:border-violet-300/60" data-testid="llm-alerts-extra-input" />
                <button onClick={add} disabled={busy || !value.trim()} data-testid="llm-alerts-add-btn"
                        className="px-2.5 py-1.5 border border-white/15 rounded-md text-[12px] hover:bg-white/5 inline-flex items-center gap-1 disabled:opacity-40 transition-colors">
                    <Plus className="w-3.5 h-3.5" /> Add
                </button>
            </div>
        </div>
    );
}

function AlertState({ state }) {
    if (!state?.last_check_at) return <p className="mt-4 text-[11px] text-white/40" data-testid="llm-alerts-state">No check has run yet.</p>;
    const open = state.open_issues || [];
    return (
        <p className="mt-4 text-[11px] text-white/55" data-testid="llm-alerts-state">
            Last check {new Date(state.last_check_at).toLocaleString()} · {open.length ? `${open.length} open problem(s)` : "no open problems"}
            {state.since && ` since ${new Date(state.since).toLocaleString()}`}
            {state.last_sent_at && ` · last alert ${new Date(state.last_sent_at).toLocaleString()}`}
        </p>
    );
}

function RecentSends({ recent }) {
    if (!recent?.length) return null;
    return (
        <ul className="mt-3 space-y-1" data-testid="llm-alerts-recent">
            {recent.slice(0, 5).map((r, i) => (
                <li key={i} className="text-[11px] flex gap-2 flex-wrap" data-testid={`llm-alerts-recent-${i}`}>
                    <span className="text-white/45 font-mono">{new Date(r.at).toLocaleString()}</span>
                    <span className="text-white/80">{KIND[r.kind] || r.kind}</span>
                    <span className={r.ok ? "text-emerald-300" : "text-rose-300"}>
                        {r.ok ? `sent to ${(r.delivered || r.to).length}` : "not sent"}
                    </span>
                    {r.error && <span className="text-white/40 break-all min-w-0">{r.error.slice(0, 140)}</span>}
                </li>
            ))}
        </ul>
    );
}
